import { useEffect, useMemo, useState } from 'react';
import { Row, Col, Container, Badge, Placeholder, Card, Button, Ratio } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import MakeRequest from '../Components/Helpers/MakeRequest';
import { useNavigate } from 'react-router-dom';

// Small, focused card for a single product
const ProductCard = ({ product, l }) => {
  const description = l(product.contentSwe, product.contentEng);
  return (
    <Card className="h-100 border-0 shadow-sm hover-shadow transition-all">
      {product.imageSrc ? (
        <Ratio aspectRatio="16x9" className="bg-light">
          <Card.Img
            loading="lazy"
            src={product.imageSrc.file || "https://images.freeimages.com/fic/images/icons/2770/ios_7_icons/512/paint_bucket.png?fmt=webp&h=350"}
            alt={product.title || product.name}
            className="object-fit-cover"
          />
        </Ratio>
      ) : (
        <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: 180 }}>
          <span className="text-muted">{l('Ingen bild', 'No image')}</span>
        </div>
      )}

      <Card.Body className='m-0 p-0 pt-2'>
        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
          <Card.Title as="h5" className="mb-0 text-truncate" title={product.name}>
            {product.name}
          </Card.Title>
          {product.tag && (
            <Badge bg="secondary" className="text-uppercase" style={{ whiteSpace: 'nowrap' }}>
              {product.tag}
            </Badge>
          )}
        </div>
        <Card.Text className="text-muted" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: '0.9rem' }}>
          {description}
        </Card.Text>
        {product.ctaHref && (
          <div className="mt-auto">
            <Button variant="outline-primary" size="sm" href={product.ctaHref}>
              {l('Läs mer', 'Learn more')}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// Skeleton while loading
const ProductSkeleton = () => (
  <Card className="h-100 border-0 shadow-sm">
    <div className="bg-light" style={{ height: 180 }} />
    <Card.Body>
      <Placeholder as={Card.Title} animation="glow">
        <Placeholder xs={8} />
      </Placeholder>
      <Placeholder as={Card.Text} animation="glow">
        <Placeholder xs={12} />
        <Placeholder xs={10} />
        <Placeholder xs={6} />
      </Placeholder>
      <Placeholder.Button variant="outline-primary" xs={4} />
    </Card.Body>
  </Card>
);

const SectionHeader = ({ title, subtitle }) => (
  <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
    <h4 className="mb-1">{title}</h4>
    {subtitle && (
      <span className="text-muted small">{subtitle}</span>
    )}
  </div>
);

const Products = () => {
  const { l } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await MakeRequest(null, 'content/products', 'get');
        if (response?.data) {
          const items = response.data.filter((obj) => obj.type !== 'productTypes');
          setProducts(items || []);
        }
      } catch (e) {
        setError(e?.message || 'Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const navigate = useNavigate();

  const handleProductClick = (product) => {
    navigate(`/products/${product.name}`, { state: { product } });
  };

  const bestSellers = useMemo(() => {
    return products.filter(p => p.prioritized);
  }, [products]);

  return (
    <>
      <Container className="my-lg-5 my-3">
        <div className="mb-5">
          <h1 className="mb-3 fw-bold">
            {l('Produkter', 'Products')}
          </h1>
          <p className="text-muted lead mb-0">
            {l(
              'Nedan följer en katalog över vår produktserie.',
              'Below is a catalogue of our product range.'
            )}
          </p>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {l('Kunde inte ladda produkter.', 'Could not load products.')} {error}
          </div>
        )}

        {bestSellers.length > 0 && (
          <div className="mb-5">
            <SectionHeader title={l('Huvudprodukter', 'Main products')} />
            <Row className="g-3">
              {bestSellers.map((product, i) => (
                <Col key={`${product.id || product.name}-${i}`} xxl={4} xl={4} lg={6} md={6} sm={12} style={{ cursor: 'pointer' }} onClick={() => handleProductClick(product)}>
                  <ProductCard product={product} l={l} />
                </Col>
              ))}
            </Row>
          </div>
        )}
        <SectionHeader
          title={l('Alla produkter', 'All products')}
          subtitle={loading ? undefined : `${products.length} ${l('produkter', 'products')}`}
        />
        <Row className="g-3">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
              <Col key={i} xxl={4} xl={4} lg={6} md={6} sm={12}>
                <ProductSkeleton />
              </Col>
            ))
            : products.map((product, i) => (
              <Col key={`${product.id || product.name}-${i}`} xxl={4} xl={4} lg={6} md={6} sm={12} style={{ cursor: 'pointer' }} onClick={() => handleProductClick(product)}>
                <ProductCard product={product} l={l} />
              </Col>
            ))}
        </Row>

        {!loading && !products?.length && (
          <div className="text-center text-muted mt-5">
            <h5 className="mb-2">{l('Inga produkter att visa', 'Nothing to show yet')}</h5>
            <p className="mb-0">{l('Kom tillbaka senare.', 'Please check back later.')}</p>
          </div>
        )}
      </Container>
    </>
  );
};

export default Products;
